import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { actions, actionTypes } from '@lbmx/app-state';
import { LoggerService } from '@lbmx/phoenix-lib-core';
import {
  SpinnerService,
  ToastrNotificationService,
} from '@lbmx/phoenix-lib-utils';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as i18nIsoCountries from 'i18n-iso-countries';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { IConfigCountry } from 'src/app/dtos/registration/registration.dto';
import {
  IOTPResponse,
  IUserProfileForm,
} from 'src/app/dtos/userProfile/userProfile.dto';
import {
  IUserNotification,
  NotificationTypes,
} from 'src/app/models/user.model';
import { ConfigProvider } from 'src/app/provider/config-provider';
import { UserProfileService } from '../../../services/user-profile/user-profile.service';
@Component({
  selector: 'app-user-profile-container',
  templateUrl: './user-profile-container.html',
  styleUrls: [],
})
export class UserProfileContainerComponent implements OnInit, OnDestroy {
  public userProfileDetails$: Observable<any> = of({
    userDetails: {
      firstName: '',
      lastName: '',
      userName: '',
      OTPRequired: null,
      phone: '',
      phoneExtension: '',
      phoneCountry: '',
      languageCode: '',
      timeZoneCode: '',
      dateTypeKey: 0,
      isTermsSigned: 0,
    },
  });
  public userNotifications$: Observable<IUserNotification[]> = of([]);
  public defaultCountryCode$: Observable<string>;
  public countries$: Observable<any[]>;
  public dateFormatTypes = [];
  public timeZoneCodes = [];
  public languageCodes = [];
  public timeZoneTypes$: Observable<any[]>;
  public fetching = false;
  public subscriptions: Subscription[] = [];
  public otpToggleResponse: IOTPResponse;
  public showWizardParam = false;

  constructor(
    private loggerService: LoggerService,
    private toastSrv: ToastrNotificationService,
    private spinnerService: SpinnerService,
    private userProfileService: UserProfileService,
    private translocoService: TranslocoService,
    private store: Store,
    private configPrv: ConfigProvider,
    private actions$: Actions,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  public async ngOnInit() {
    this.spinnerService.on();
    this.route.queryParams
      .pipe(
        tap((params) => {
          this.showWizardParam = JSON.parse(params?.wizard || false);
          if (this.showWizardParam) {
            this.spinnerService.off();
          }
        })
      )
      .subscribe();
    this.fetching = true;
    this.defaultCountryCode$ = this.userProfileService.getAccount().pipe(
      map((account) => account.details.country),
      catchError(() => of('CAN'))
    );
    this.countries$ = this.userProfileService.getCountries().pipe(
      map((countries: IConfigCountry[]) =>
        countries.filter(
          (country) =>
            i18nIsoCountries.alpha3ToAlpha2(country?.countryCode) !== undefined
        )
      ),
      map((countries: IConfigCountry[]) =>
        countries.map((country) => {
          return {
            label: `${country.countryName} (${i18nIsoCountries.alpha3ToAlpha2(
              country.countryCode
            )})`,
            value: i18nIsoCountries.alpha3ToAlpha2(country.countryCode),
          };
        })
      )
    );
    this.userProfileDetails$ = this.userProfileService.getUserProfile().pipe(
      tap((response) => {
        this.loggerService.info(
          'getUserProfileResponse',
          'UserProfileService.getUserProfile',
          JSON.stringify(response)
        );
      }),
      map((response) => {
        return {
          firstName: response.userDetails.firstName,
          lastName: response.userDetails.lastName,
          userName: response.userDetails.username,
          OTPRequired: response.userDetails.otpRequired,
          phoneCountry: response.userDetails.phoneCountry,
          phone: response.userDetails.phone,
          phoneExtension: response.userDetails.phoneExtension,
          languageCode: response.userDetails.languageCode,
          timeZoneCode: response.userDetails.timeZoneCode,
          dateTypeKey: response.userDetails.dateTypeKey,
          isTermsSigned: response.userDetails.isTermsSigned,
        };
      }),
      finalize(() => {
        this.fetching = false;
        this.spinnerService.off();
      })
    );
    // Get all user notifications configuration
    this.userNotifications$ = this.userProfileService
      .getUserNotifications()
      .pipe(
        map((response) => {
          const result = response.userNotificationList.map((notification) => {
            let label = '';
            let checkboxCtl = '';
            let intervalCtl = '';
            switch (notification.notificationTypeKey) {
              case NotificationTypes.POA:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.POA'
                );
                checkboxCtl = 'poaReceived';
                intervalCtl = 'poaInterval';
                break;
              case NotificationTypes.PO:
                label = this.translocoService.translate('USER_NOTIFICATION.PO');
                checkboxCtl = 'poReceived';
                intervalCtl = 'poInterval';
                break;
              case NotificationTypes.Invoice:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.INVOICE'
                );
                checkboxCtl = 'invoiceReceived';
                intervalCtl = 'invoiceInterval';
                break;
              case NotificationTypes.ASN:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.ASN'
                );
                checkboxCtl = 'asnReceived';
                intervalCtl = 'asnInterval';
                break;
              case NotificationTypes.CollectionBatch:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.COLLECTION_BATCH'
                );
                checkboxCtl = 'collectionBatch';
                intervalCtl = 'collectionBatchInterval';
                break;
              case NotificationTypes.InvoiceKeptOnHold:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.INVOICE_ON_HOLD'
                );
                checkboxCtl = 'invoiceKeptOnHold';
                intervalCtl = 'invoiceKeptOnHoldInterval';
                break;
              case NotificationTypes.InvoiceMissing:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.INVOICE_MISSING'
                );
                checkboxCtl = 'invoiceMissing';
                intervalCtl = 'invoiceMissingInterval';
                break;
              case NotificationTypes.RemittancesReceived:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.REMITTANCE'
                );
                checkboxCtl = 'remittanceReceived';
                intervalCtl = 'remittanceInterval';
                break;
              case NotificationTypes.ReceivedPAD:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.PAD'
                );
                checkboxCtl = 'padReceived';
                intervalCtl = 'padInterval';
                break;
              case NotificationTypes.OS:
                label = this.translocoService.translate('USER_NOTIFICATION.OS');
                checkboxCtl = 'osReceived';
                intervalCtl = 'osInterval';
                break;
              case NotificationTypes.POC:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.POC'
                );
                checkboxCtl = 'pocReceived';
                intervalCtl = 'pocInterval';
                break;
              case NotificationTypes.AA:
                label = this.translocoService.translate('USER_NOTIFICATION.AA');
                checkboxCtl = 'aaReceived';
                intervalCtl = 'aaInterval';
                break;
              case NotificationTypes.TM:
                label = this.translocoService.translate('USER_NOTIFICATION.TM');
                checkboxCtl = 'tmReceived';
                intervalCtl = 'tmInterval';
                break;
              case NotificationTypes.STATEMENT:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.STATEMENT'
                );
                checkboxCtl = 'statementReceived';
                intervalCtl = 'statementInterval';
                break;
              case NotificationTypes.REBATESTATEMENT:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.REBATESTATEMENT'
                );
                checkboxCtl = 'rebateStatementReceived';
                intervalCtl = 'rebateStatementInterval';
                break;
              case NotificationTypes.CDA:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.CDA'
                );
                checkboxCtl = 'cdaReceived';
                intervalCtl = 'cdaInterval';
                break;
              case NotificationTypes.DSDSI:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.DSDSI'
                );
                checkboxCtl = 'dsdsiReceived';
                intervalCtl = 'dsdsiInterval';
                break;
              case NotificationTypes.PSRC:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.PSRC'
                );
                checkboxCtl = 'psrcReceived';
                intervalCtl = 'psrcInterval';
                break;
              case NotificationTypes.PI:
                label = this.translocoService.translate('USER_NOTIFICATION.PI');
                checkboxCtl = 'piReceived';
                intervalCtl = 'piInterval';
                break;
              case NotificationTypes.WSO:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.WSO'
                );
                checkboxCtl = 'wsoReceived';
                intervalCtl = 'wsoInterval';
                break;
              case NotificationTypes.WSTSA:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.WSTSA'
                );
                checkboxCtl = 'wstsaReceived';
                intervalCtl = 'wstsaInterval';
                break;
              case NotificationTypes.WSTRA:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.WSTRA'
                );
                checkboxCtl = 'wstraReceived';
                intervalCtl = 'wstraInterval';
                break;
              case NotificationTypes.WSA:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.WSA'
                );
                checkboxCtl = 'wsaReceived';
                intervalCtl = 'wsaInterval';
                break;
              case NotificationTypes.WIAA:
                label = this.translocoService.translate(
                  'USER_NOTIFICATION.WIAA'
                );
                checkboxCtl = 'wiaaReceived';
                intervalCtl = 'wiaaInterval';
                break;
              default:
                break;
            }
            return {
              ...notification,
              label,
              checkboxCtl,
              intervalCtl,
            };
          });

          return result;
        }),
        finalize(() => {
          this.spinnerService.off();
        })
      );

    /* Get list of DateFormat Types */
    this.dateFormatTypes = await this.userProfileService
      .getdateFormatTypes()
      .toPromise()
      .then((res) => {
        if (res.dateFormatTypeResponse != null) {
          return res.dateFormatTypeResponse.map((a) => {
            return { value: a.dateTypeKey, label: a.description };
          });
        }
      });

    /* Get list of Language Types */
    this.languageCodes = await this.userProfileService
      .getLanguageTypes()
      .toPromise()
      .then((res) => {
        if (res.languageTypes != null) {
          return res.languageTypes.map((a) => {
            return { value: a.languageCode, label: a.languageDescription };
          });
        }
      });
    /* Get list of Timezone Types */
    this.timeZoneCodes = await this.userProfileService
      .getTimezoneTypes()
      .toPromise()
      .then((res) => {
        if (res.timeZoneTypeResponse != null) {
          return res.timeZoneTypeResponse.map((a) => {
            return { value: a.timeZoneCode, label: a.timeZoneDescription };
          });
        }
      });
  }

  public ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
    this.spinnerService.off();
  }

  public toggleOTP(data) {
    this.spinnerService.on();
    const req = data as IUserProfileForm;
    let otpToggleResponse: IOTPResponse;
    this.subscriptions.push(
      this.userProfileService.updateUserProfile(req).subscribe(
        (res) => {
          otpToggleResponse = {
            success: true,
            message: res.status,
          };
        },
        (err) => {
          otpToggleResponse = {
            success: false,
            message: err.errorMessage,
          };
        },
        () => {
          this.otpToggleResponse = otpToggleResponse;
          this.spinnerService.off();
        }
      )
    );
  }

  public handleSave(data) {
    this.spinnerService.on();
    /* Eliminate null values before updateProfile endpoint call  */
    const request = this.filterEmptyFields(data) as IUserProfileForm;
    if (request.confirmPassword) {
      delete request.confirmPassword;
    }

    this.store.dispatch(
      actions.updateUserProfileFromForm({
        userProfile: { ...request, dateType: request.dateTypeKey },
        endPoints: this.configPrv.AppSetting,
      })
    );
    this.subscriptions.push(
      this.actions$
        .pipe(
          ofType(
            actions.updateUserProfileSuccess,
            actions.updateUserProfileFailure
          ),
          map((action) => {
            if (action.type === actionTypes.UPDATE_USER_PROFILE_SUCCESS) {
              this.loggerService.info(
                'UpdateUserResponse',
                'UserProfileService.updateUserProfile'
              );
              this.toastSrv.successNotify(
                this.translocoService.translate('COMMON.CHANGES_SAVED')
              );
              this.translocoService.setActiveLang(
                request.languageCode.split('-')[0]
              );
              if (request['password']) {
                this.router.navigate(['/logout']);
              }
            } else {
              this.loggerService.error(
                'UpdateUserResponse',
                'UserProfileService.updateUserProfile'
              );
            }
            this.spinnerService.off();
          })
        )
        .subscribe()
    );
  }

  public saveUserNotifications(data) {
    this.spinnerService.on();

    this.subscriptions.push(
      this.userProfileService
        .updateUserNotifications(data)
        .pipe(
          tap(() =>
            this.toastSrv.successNotify(
              this.translocoService.translate('COMMON.CHANGES_SAVED')
            )
          ),
          catchError(() =>
            of(
              this.toastSrv.errorNotify(
                this.translocoService.translate(
                  'SUPPLIER.FAILURE_TOAST_MESSAGE'
                )
              )
            )
          ),
          finalize(() => this.spinnerService.off())
        )
        .subscribe()
    );
  }
  /** Function to filter null values from userProfileForm */
  private filterEmptyFields(data) {
    return Object.entries(data).reduce((prev, [field, value]) => {
      if (value !== '' && value !== null) {
        return { ...prev, [field]: value };
      } else {
        return prev;
      }
    }, {});
  }
}
